import { graphql } from 'gatsby';
import React from 'react';
import { useSanityData } from '../../../hooks';
import { Seo } from '../../common';
import { Layout } from '../../layouts';
import {
  AlternatingRows,
  Article,
  BenefitsOverview,
  CalendarFull,
  CaseStudies,
  Checklist,
  CtaSelector,
  FormFull,
  HeroCalendar,
  HeroContact,
  HeroForm,
  HeroGraphic,
  HeroTestimonial,
  HeroTextOnly,
  ImageGallery,
  MapFull,
  ServicesOverview,
  TableSection,
  Testimonials,
  TrustLogos,
  UtilitySelector,
} from '../sections';

export const query = graphql`
  query PageTemplateQuery($id: String!) {
    page: sanityPage(id: { eq: $id }) {
      ...ContentAlternatingRowsFragment
      ...ContentArticleFragment
      ...ContentBenefitsOverviewFragment
      ...ContentCaseStudiesFragment
      ...ContentChecklistFragment
      ...ContentCtaFragment
      ...ContentImageGalleryFragment
      ...ContentServicesOverviewFragment
      ...ContentTableFragment
      ...ContentTestimonialsFragment
      ...ContentTrustLogosFragment
      ...ContentUtilityFragment
      ...PageGbpFragment
      ...PageHeroFragment
      ...PageNavigationFragment
      ...PageSeoFragment
    }
  }
`;

const PageTemplate = ({ data }) => {
  const page = data?.page;
  const sanity = useSanityData();

  // Define hero section components mapping
  const heroSectionComponents = {
    textForm: HeroForm,
    textCalendar: HeroCalendar,
    textTestimonial: HeroTestimonial,
    textContactForm: HeroContact,
    textContactMap: HeroContact,
    textOnly: HeroTextOnly,
    textGraphic: HeroGraphic,
  };

  // Define content section components mapping
  const contentSectionComponents = {
    sectionAlternatingRows: AlternatingRows,
    sectionArticle: Article,
    sectionCaseStudies: CaseStudies,
    sectionChecklist: Checklist,
    sectionCta: CtaSelector,
    sectionBenefitsOverview: BenefitsOverview,
    sectionImageGallery: ImageGallery,
    sectionServicesOverview: ServicesOverview,
    sectionTable: TableSection,
    sectionTestimonials: Testimonials,
    sectionTrustLogos: TrustLogos,
    sectionUtility: UtilitySelector,
  };

  // Build hero section component based on the page template, fallback to HeroForm
  const HeroSectionComponent = heroSectionComponents[page?.heroTemplate] || HeroForm;

  // Filter content sections that have an associated component
  const filteredContentSections = page?.contentSections?.filter(
    section => contentSectionComponents[section._type]
  );

  // Conditional rendering for Map and Form/Calendar (if enabled in sanity)
  const renderMap = sanity?.map?.addMapToAllPages ? <MapFull page={page} sanity={sanity} /> : null;

  let renderFormOrCalendar = null;

  if (sanity?.form?.addCalendarToAllPages) {
    renderFormOrCalendar = <CalendarFull page={page} sanity={sanity} />;
  } else if (sanity?.form?.addFormToAllPages) {
    renderFormOrCalendar = <FormFull page={page} sanity={sanity} />;
  }

  return (
    <Layout page={page} sanity={sanity}>
      <Seo page={page} sanity={sanity} />
      {page?.heroTemplate !== 'none' && <HeroSectionComponent page={page} sanity={sanity} />}
      {filteredContentSections?.map((section, index) => {
        const uniqueKey = `${section._key}-${index}`;
        const ContentSectionComponent = contentSectionComponents[section._type];
        return ContentSectionComponent ? (
          <ContentSectionComponent key={uniqueKey} page={page} sanity={sanity} section={section} />
        ) : null;
      })}
      {renderMap}
      {renderFormOrCalendar}
    </Layout>
  );
};

export default PageTemplate;
